<template>
  <div class="main-content">
    <!-- Page Header Start -->
    <PageHeader>
      <PageHeaderTitleContent>
        <TitleTextContent
          :titleText="'Products & Services Details'"
        />
        <router-link :to="{ name: 'tool-list' }">
          <v-btn
            rounded
            large
            dark
            :color="'#424EC4'"
            >
            Back to All Products & Services
          </v-btn>
        </router-link>
      </PageHeaderTitleContent>
    </PageHeader>
    <!-- Page Header End -->

    <section class="section section-blog p-0 mt-1 mb-3">
      <!-- Social Media Section Start -->
    <SocialMediaWidgets
      :title="title"
      :description="description"
      :type="'tool'"
    />
    <!-- Social Media Section End -->

      <div class="container-fluid px-xs-1 px-sm-1 px-md-5 px-lg-4 px-xl-4">
        <article>
          <!-- Company Information -->
          <div class="w-100 mb-3 mb-lg-5 mb-xl-5">
            <b-row class="h-100">
              <b-col md="4" lg="4" xl="4" sm="12" xs="12" class="d-flex flex-column justify-content-center">
                <img
                  :src="item?.mainImage"
                  :alt="item?.photoDescription"
                  class="img-fluid img-thumbnail border-0 tool-logo"
                />
              </b-col>
              <b-col class="pb-0 pl-3 pl-lg-5 pl-xl-5" md="8" lg="8" xl="8" sm="12" xs="12">
                <div class="company-info-name d-flex flex-column justify-content-start align-items-center align-items-lg-start align-items-xl-start pt-lg-4 pt-xl-4">
                  {{ item?.toolCreatedBy }}
                </div>
                <div class="company-info-data d-flex flex-column justify-content-center justify-content-lg-end justify-content-xl-end" :class="companyDataInfoClass">
                  <div class="company-info-data-items">
                    <div class="w-100 d-flex justify-content-start align-items-start company-info-data-item">
                      <div class="company-info-data-item-title">Cost Model:</div>
                      <div class="company-info-data-item-value">{{ item?.toolCostModel }}</div>
                    </div>
                    <div class="w-100 d-flex justify-content-start align-items-start company-info-data-item">
                      <div class="company-info-data-item-title">Ease of Use:</div>
                      <div class="company-info-data-item-value">{{ item?.toolEaseOfUse }}</div>
                    </div>
                    <div class="w-100 d-flex justify-content-start align-items-start company-info-data-item">
                      <div class="company-info-data-item-title">Typical Users:</div>
                      <div class="company-info-data-item-value" :title="item?.toolTypicalUsers">
                          <span class="company-info-data-value-overflow">{{ item?.toolTypicalUsers }}</span>
                      </div>
                    </div>
                    <div class="w-100 d-flex justify-content-start align-items-start company-info-data-item">
                      <div class="company-info-data-item-title">Free Training Resources:</div>
                      <div class="company-info-data-item-value">
                        <font-awesome-icon
                          v-if="item?.toolFreeTrainingResourcesAvailable === 'true'"
                          class="pl-1"
                          size="lg"
                          :icon="['fas', 'check-circle']"
                        />
                        <font-awesome-icon
                          v-else
                          class="pl-1"
                          size="lg"
                          :icon="['fas', 'times-circle']"
                        />
                      </div>
                    </div>
                    <div class="w-100 d-flex justify-content-start align-items-start company-info-data-item">
                      <div class="company-info-data-item-title">Paid Training Resources:</div>
                      <div class="company-info-data-item-value">
                        <font-awesome-icon
                          v-if="item?.toolPaidTraining === 'true'"
                          class="pl-1"
                          size="lg"
                          :icon="['fas', 'check-circle']" 
                        />
                        <font-awesome-icon
                          v-else
                          class="pl-1"
                          size="lg"
                          :icon="['fas', 'times-circle']"
                        />
                      </div>
                    </div>
                    <div class="w-100 d-flex justify-content-start align-items-start company-info-data-item">
                      <div class="company-info-data-item-title">Website:</div>
                      <div class="company-info-data-item-value">
                        <a
                          style="word-break: break-all;"
                          target="_blank"
                          :href="item?.toolWebsite"
                          >
                          {{ item?.toolWebsite }}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Company Information Description -->
          <div class="w-100 pt-1 mb-4">
            <div class="w-100 my-3 company-description-header text-center text-lg-left text-xl-left">About This Listing</div>
            <div class="company-description-text p-0">
              <span v-html="item?.review"></span>
            </div>
          </div>

          <!-- Certificates & Clients -->
          <div class="w-100 mb-4">
            <b-row>
              <!-- Certificates Start -->
              <b-col xs="12" sm="12" md="6" lg="6" xl="6" class="certs-and-client-row">
                <p class="mb-0 title text-center text-lg-left text-xl-left"><b>Related Certifications</b></p>
                <div class="company-certs-clients-container align-self-center align-self-lg-start align-self-xl-start">
                  <b-list-group
                    class="w-100"
                    v-if="item?.toolRelatedCertifications?.certification.length > 0"
                  >
                    <b-list-group-item
                      v-for="cert in item?.toolRelatedCertifications?.certification"
                      :key="cert"
                    >{{ cert }}
                    </b-list-group-item>
                  </b-list-group>
                  <b-list-group 
                    class="w-100"
                    v-else
                  >
                    <b-list-group-item>Certificates are unavailable</b-list-group-item>
                  </b-list-group>
                </div>
              </b-col>
              <!-- Certificates End -->
              
              <!-- Clients Start -->
              <b-col xs="12" sm="12" md="6" lg="6" xl="6" class="certs-and-client-row">
                <p class="mb-0 title text-center text-lg-left text-xl-left"><b>Known Clients</b></p>
                <div class="company-certs-clients-container align-self-center align-self-lg-start align-self-xl-start">
                  <b-list-group
                    class="w-100"
                    v-if="item?.toolKnownClients?.knownClient.length > 0"
                  >
                    <b-list-group-item
                      v-for="cert in item?.toolKnownClients?.knownClient"
                      :key="cert"
                    >{{ cert }}
                    </b-list-group-item>
                  </b-list-group>
                  <b-list-group 
                    class="w-100"
                    v-else
                  >
                    <b-list-group-item>Clients are unavailable</b-list-group-item>
                  </b-list-group>
                </div>
              </b-col>
              <!-- Clients End -->
            </b-row>
          </div>
        </article>
      </div>
    </section>
    <!-- Blog Details Section End-->
  </div>
</template>
<script>
import { BFormInput } from 'bootstrap-vue';
import axios from 'axios';
import SocialMediaWidgets from '../library/widgets/social-media-widgets.vue';
// import { handleApiError } from '../../utils/authentication';
import PageHeader from "../library/widgets/page-header.vue";
import PageHeaderTitleContent from "../library/widgets/page-header-title-content.vue";
import TitleTextContent from '../library/widgets/title-text-content.vue';

export default {
  name: 'tool-detail',
  components: {
    BFormInput,
    SocialMediaWidgets,
    PageHeader,
    PageHeaderTitleContent,
    TitleTextContent,
  },
  props: {
    id: {
      type: String,
      default: null,
    },  },
  data() {
    return {
      isLoading: false,
      query: '',
      item: {},
      comments: [],
      addComment: '',
      linkCopyText: 'Click to copy url link',
      commentPosted: false,
      commentPosting: false,
      // Pagination
      searchQuery: '',
      commentCount: 0,
      currentPage: 1,
      perPage: 3,
       // Query String
      queryValue: this.id, 
      queryString: "id"
    };
  },
  async created() {
    if (this.toolId) {
      await this.getResource(this.toolId);
      // await this.getToolComments(this.toolId);
    } else {
      this.$router.push({ path: 'tool-list' });
    }
  },
  mounted() {
    this.init();
  },
  computed: {
    toolId() {
      return this.id || this.$route.params.id;
    },
    title() {
      return this.item?.title || 'Title';
    },
    description() {
      return this.item?.description || 'Description';
    },
    headline() {
      return this.item?.headLine || 'HeadLine';
    },
  },
  methods: {
    init() {
      /* The footer height no longer needs to be set */
      // this.initFooterHeight();
    },
    initFooterHeight() {
      var footerHeight = $('footer').outerHeight();
      $('.main-content').css('margin-bottom', footerHeight);
    },
    async getResource(id) {
      /*  API call to server: GET /api/resources/blogs */
      axios
        .get(`/api/resources/toolReviews/${id}`)
        .then((response) => {
          if (response.data.Items.length > 0) {
            /** Does an item exist for the query id */
            this.item = response.data.Items[0];
            //console.log('Tool', response.data);
          } else {
            /** If no item exists with the id, redirect to list*/
            this.$router.push({ path: 'tool-list' });
          }
        })
        .catch((error) => {
          /** On error, redirect back to list */
          console.error(error);
          this.$router.push({ path: 'tool-list' });
        });
    },
    /**** IT DOESNT SEEM LIKE THIS API: "/api/toolcomments/approved/" IS WORKING, AND ITS NOT BEING USED IN THE SITE ****/
    /*
    async getToolComments(id) {

      axios
        .get(`/api/toolcomments/approved/${id}`)
        .then((response) => {

          if (response.data.length >= 0) {

            this.comments = response.data;
          } else {

            this.$router.push({ path: 'tool-list' });
          }
        })
        .catch((error) => {

          // handleApiError(error, this.$router);
        });
    },
    */
   /**** IT DOESNT SEEM LIKE THIS API: "/api/toolcomments/approved/" IS WORKING, AND ITS NOT BEING USED IN THE SITE ****/
   /*
    async postToolComment(comment, id) {
      this.commentPosting = true;

      axios
        .post(`/api/toolcomments`, {
          commentBody: comment,
          reviewId: id,
        })
        .then((response) => {
          //console.log('Post Comment Response', response.data);
          // this.commentPosted = true;
        })
        .catch((error) => {

          // handleApiError(error, this.$router);
        })
        .finally(() => {
          this.addComment = '';
          this.commentPosting = false;
        });
    },
    */
    pageClicked() {
      // Do not scroll on this page
      // window.scrollTo({top: 0, behavior: 'smooth'})
    },
    onHidden() {
      // Return focus to the button once hidden
      this.$refs.button.focus();
    },
    activateSigninRegisterMenuItem(){
      EventBus.$emit("activate-indicated-menu-item", "Sign In/Register");
    },
  },
};
</script>
<style scoped>
.centered-link {
  display: flex;
  justify-content: center;
  align-items: center;
}
.comment-date {
  font-size: 14px;
}
#comment-search {
  height: 50px;
}
.title {
  font-size: 20px;
}
.descriptor {
  font-size: 17px;
}
#social-share {
  margin-left: -0.25rem;
}
.tool-logo {
  height: 100%;
  width: 100%;
  object-fit: contain;
  max-height: 350px;
}
.social-media-container {
  border-top: 1px solid #000000;
  border-bottom: 1px solid #000000;
}
.company-info-name {
  width: 100%;
  align-items: flex-start;
  color: #272727;
  font-family: GeomGraphic;
  font-style: normal;
  font-weight: 400;
  font-size: clamp(28px, 2vw, 32px);
  line-height: normal;
}
.company-info-data {
  width: 100%;
  height: 245px;
}
.company-info-data-items {
  width: 100%;
  height: 95%;
  display: flex;
  flex-direction: column;
}
.company-info-data-item {
  height: 45px;
  padding-top: 10px;
}
.company-info-data-item-title, .company-description-header {
  color: #272727;
  font-family: GeomGraphic;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  height: 100%;
  width: auto;
}
.company-info-data-item-value, .company-description-text {
  color: #000;
  font-family: Rajdhani;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  height: 100%;
  padding-left: 15px;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
}
.company-info-data-value-overflow {
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.certs-and-client-row {
  display: flex;
  flex-direction: column;
}
.company-certs-clients-container {
  height: auto;
  width: 80%;
}
.list-group-item {
  border-color: black;
}

@media (1000px < width <= 1200px) {
  .company-info-data-value-overflow {
    width: 400px;
  }
}

@media (800px < width <= 1000px) {
  .company-info-data-value-overflow {
    width: 300px;
  }
}

@media (600px < width <= 800px) {
  .company-info-data-value-overflow {
    width: 250px;
  }
}

@media (500px < width <= 600px) {
  .company-info-data-value-overflow {
    width: 200px;
  }
}

@media (width <= 500px) {
  .company-info-data-value-overflow {
    width: 150px;
  }
}
</style>
